import { useCallback } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';

import { CONFIRM_POPUP_MODE, useConfirmPopup } from 'src/components/ConfirmPopup';
import { useHistory } from 'src/lib/hooks';
import { ROUTES } from 'src/routes';
import { useErrorHandler } from 'src/services/useErrorHandler';
import { RESTRICT_ACTION_TYPE, useRestrictActionPopup } from 'src/services/useRestrictActionPopup';
import { useCustomerActions } from 'src/store/customer';
import { useNotificationActions } from 'src/store/notification';
import { useVisualLanguageActions } from 'src/store/visualLanguage';

export const useDeleteVisualLanguage = () => {
  const confirmPopup = useConfirmPopup();
  const history = useHistory();
  const location = useLocation();
  const customerActions = useCustomerActions();
  const visualLanguageActions = useVisualLanguageActions();
  const notificationActions = useNotificationActions();
  const handleError = useErrorHandler();
  const openRestrictActionPopup = useRestrictActionPopup();

  return useCallback(
    (visualLanguage) =>
      new Promise((resolve) => {
        const onConfirm = async () => {
          try {
            await visualLanguageActions.deleteVisualLanguage(visualLanguage.id);

            notificationActions.displaySuccess(`Visual language '${visualLanguage.name}' deleted`);
            if (location.pathname.startsWith(`${ROUTES.VISUAL_LANGUAGE}/${visualLanguage.id}`)) {
              history.push(ROUTES.ROOT);
            }
            customerActions.refreshCurrentCustomerFeatureLimit();
            resolve(true);
          } catch (error) {
            handleError(error);
            resolve(false);
          }
        };

        const onCancel = () => {
          resolve(false);
        };

        if (!visualLanguage) {
          resolve(false);
          return;
        }

        const workspaceVisualLanguages = visualLanguageActions.getWorkspaceVisualLanguages();
        if (workspaceVisualLanguages.filter((item) => item.id !== visualLanguage.id).length === 0) {
          openRestrictActionPopup({ actionType: RESTRICT_ACTION_TYPE.deleteLastVisualLanguage });
          return;
        }

        confirmPopup({
          mode: CONFIRM_POPUP_MODE.warning,
          title: 'Delete visual language?',
          content:
            'This will permanently remove all the assets in this visual language. This action can’t be undone.',
          buttons: {
            confirm: 'Delete',
            cancel: 'Cancel',
          },
          onConfirm,
          onCancel,
        });
      }),
    [
      confirmPopup,
      visualLanguageActions,
      notificationActions,
      location.pathname,
      customerActions,
      history,
      handleError,
      openRestrictActionPopup,
    ]
  );
};
